import type { PaymentMethodSummary } from '|/services/purchase-payments';

export type PaymentStatusKind = 'succeeded' | 'processing' | null;

const paymentStatusesToDisplay = ['Succeeded', 'Processing', 'Created'];

export interface PaymentForDisplay {
  paymentId: string;
  amount: number;
  status: string;
  paymentMethod?: string;
  createdOnUtc: Date;
  description?: string;
}

const isShippingDescription = (description: string | undefined) =>
  description?.toLowerCase().includes('shipping') ?? false;

export const filterPaymentsForDisplay = <T extends { status: string }>(payments: T[]): T[] =>
  payments.filter(({ status }) => paymentStatusesToDisplay.includes(status));

export const getPaymentDescriptionText = (description: string | undefined) =>
  isShippingDescription(description)
    ? 'Nonrefundable shipping fee from current purchase'
    : 'Cash down payment from current purchase';

export const getPaymentInitiatedText = (paymentMethod: string | undefined, createdOnUtc: Date) => {
  const dateText = createdOnUtc.toLocaleDateString();

  switch (paymentMethod) {
    case 'Ach':
      return `ACH initiated on ${dateText}`;
    case 'CashiersCheck':
      return `Cashier's check received on ${dateText}`;
    case 'WireTransfer':
      return `Wire transfer received on ${dateText}`;
    case 'Card':
    case 'GenericCard':
      return `Card payment processed on ${dateText}`;
    default:
      return '';
  }
};

export const getPaymentStatusDisplayText = (status: string) => {
  switch (status) {
    case 'Succeeded':
      return 'Processed';
    case 'Processing':
    case 'Created':
      return 'Processing';
    default:
      return '';
  }
};

export const getPaymentStatusKind = (status: string): PaymentStatusKind => {
  switch (status) {
    case 'Succeeded':
      return 'succeeded';
    case 'Processing':
    case 'Created':
      return 'processing';
    default:
      return null;
  }
};

/**
 * A row to render in the credit modal. `payment` is set when the row is backed
 * by an actual `paymentsOnPurchase` entry (with date + status). `payment` is
 * null when the row is synthesized from `purchasePaymentBreakdown` because the
 * customer has not initiated a payment yet (order summary stage).
 */
export interface CreditModalRow {
  key: string;
  label: string;
  amount: number;
  payment: PaymentForDisplay | null;
}

export const buildCreditModalRows = (data: PaymentMethodSummary): CreditModalRow[] => {
  const { cashDown, shippingFee, creditFromCustomerAccountBalance } = data.purchasePaymentBreakdown;
  const displayablePayments = filterPaymentsForDisplay(data.paymentsOnPurchase);

  const shippingPayments = displayablePayments.filter(p => isShippingDescription(p.description));
  const cashDownPayments = displayablePayments.filter(p => !isShippingDescription(p.description));

  const rows: CreditModalRow[] = [];

  if (cashDownPayments.length > 0) {
    cashDownPayments.forEach(p =>
      rows.push({
        key: `payment-${p.paymentId}`,
        label: getPaymentDescriptionText(p.description),
        amount: p.amount,
        payment: p
      })
    );
  } else if (cashDown > 0) {
    rows.push({
      key: 'pending-cash-down',
      label: getPaymentDescriptionText(undefined),
      amount: cashDown,
      payment: null
    });
  }

  if (shippingPayments.length > 0) {
    shippingPayments.forEach(p =>
      rows.push({
        key: `payment-${p.paymentId}`,
        label: getPaymentDescriptionText(p.description),
        amount: p.amount,
        payment: p
      })
    );
  } else if (shippingFee > 0) {
    rows.push({
      key: 'pending-shipping-fee',
      label: getPaymentDescriptionText('shipping'),
      amount: shippingFee,
      payment: null
    });
  }

  if (creditFromCustomerAccountBalance > 0) {
    rows.push({
      key: 'credit-from-previous',
      label: 'Credit from previous purchases',
      amount: creditFromCustomerAccountBalance,
      payment: null
    });
  }

  return rows;
};

export const sumRows = (rows: CreditModalRow[]): number =>
  rows.reduce((total, row) => total + row.amount, 0);
